/**
 * The sentence that *is* the chart, for anyone not looking at it.
 *
 * `04-screen-specs` asks for a sparkline in two places — E4's per-sound progress and
 * F1's level history — and a sparkline with no text alternative is nothing at all to
 * VoiceOver or TalkBack. So the wording is domain logic rather than each platform's
 * own idea: three platforms describing one chart three different ways is three
 * different products, which is the same reason `DISPUTE_REASON_LABELS` lives here.
 *
 * Ported to `SaySoonerKit/Domain/Sparkline.swift` and `core/domain/.../Sparkline.kt`
 * with the same test cases, per the port rule in `CLAUDE.md`.
 */

/** How a series moved, end to end. */
export type SparklineTrend = 'up' | 'down' | 'level';

/**
 * A change smaller than one unit is not a direction.
 *
 * Rounding 60.4 down to 60 and still calling it "up" would read as progress the
 * learner has not made, which is the one thing a progress chart must not do.
 */
export function sparklineTrend(values: readonly number[]): SparklineTrend {
  const first = values[0];
  const last = values[values.length - 1];
  if (first === undefined || last === undefined) return 'level';

  const change = last - first;
  if (Math.abs(change) < 1) return 'level';
  return change > 0 ? 'up' : 'down';
}

/** The shape of a series in words. `unit` names what the numbers are. */
export function sparklineSummary(values: readonly number[], unit: string): string {
  const first = values[0];
  const last = values[values.length - 1];
  if (first === undefined || last === undefined) return 'No attempts yet.';
  if (values.length === 1) return `One attempt, ${Math.round(first)} ${unit}.`;

  return (
    `${values.length} attempts, ${Math.round(first)} to ${Math.round(last)} ${unit}, ` +
    `trending ${sparklineTrend(values)}.`
  );
}

/**
 * Describes a run of level estimates, for F1's sparkline label.
 *
 * Separate from `sparklineSummary` because the points are not numbers a learner
 * recognises: "3 attempts, 1 to 5 sublevels" is the index talking. A level has a name,
 * so the sentence uses it.
 *
 * Takes the estimates **newest first** — the order `getProgress.cefrHistory` and
 * `getCefrDetail.history` both deliver — and describes them oldest to newest, which is
 * the direction the chart is drawn. Keeping that knowledge in one function is the point:
 * every client picked the wrong end of this array at least once.
 */
export function levelHistorySummary(newestFirst: readonly string[]): string {
  const newest = newestFirst[0];
  const oldest = newestFirst[newestFirst.length - 1];
  if (newest === undefined || oldest === undefined) return 'No level estimates yet.';
  if (newestFirst.length === 1) return `One estimate, ${newest}.`;
  // "still", not "steady at": this compares the ends, so a learner who dipped to
  // A1.2 and climbed back would be told they had held level when they had not.
  if (newest === oldest) return `${newestFirst.length} estimates, still ${newest}.`;
  return `${newestFirst.length} estimates, ${oldest} to ${newest}.`;
}

/**
 * A newest-first series turned into plot order, oldest first.
 *
 * One line, and it exists because the direction of these arrays has been got wrong on
 * every client at least once: `history.last` read as the current estimate on three
 * platforms, and a chart drawn without this shows an improving learner a line going
 * down. A named function with the order in its name is harder to get wrong than a
 * `.reverse()` somebody has to remember, and it gives the rule one place to be tested.
 */
export function plotOrder(newestFirst: readonly number[]): readonly number[] {
  return [...newestFirst].reverse();
}
